import { areArraysShallowEqual, areObjectsShallowEqual, isEqual } from '@tldraw/utils'
import { useRef } from 'react'

function useIdentity<T>(value: T, isEqual: (a: T, b: T) => boolean): T {
	const ref = useRef(value)
	if (isEqual(value, ref.current)) {
		return ref.current
	}
	ref.current = value
	return value
}

const areNullableArraysShallowEqual = (
	a: readonly any[] | null | undefined,
	b: readonly any[] | null | undefined
) => {
	a ??= null
	b ??= null
	if (a === b) {
		return true
	}
	if (!a || !b) {
		return false
	}
	return areArraysShallowEqual(a, b)
}

/** @internal */
export function useShallowArrayIdentity<T extends readonly any[] | null | undefined>(arr: T): T {
	return useIdentity(arr, areNullableArraysShallowEqual)
}

const areNullableObjectsShallowEqual = (
	a: object | null | undefined,
	b: object | null | undefined
) => {
	a ??= null
	b ??= null
	if (a === b) {
		return true
	}
	if (!a || !b) {
		return false
	}
	return areObjectsShallowEqual(a, b)
}

/** @internal */
export function useShallowObjectIdentity<T extends object | null | undefined>(obj: T): T {
	return useIdentity(obj, areNullableObjectsShallowEqual)
}

/**
 * For small option objects that may nest (e.g. camera constraints), where a shallow comparison
 * would still see a fresh identity on every render. Functions and class instances inside still
 * compare by reference, so an inline callback or a fresh `Extension.configure()` counts as a change.
 *
 * @internal
 */
export function useDeepObjectIdentity<T extends object | null | undefined>(obj: T): T {
	return useIdentity(obj, isEqual)
}
